import { useState, useEffect } from "react";
import { Tv, Check, Loader2, X, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const COLORS = ["bg-blue-600", "bg-rose-600", "bg-emerald-600", "bg-amber-600", "bg-indigo-600", "bg-cyan-700"];

export default function ChannelKeepSelector({ open, limit = 1, title, description, confirmLabel, busy: extBusy = false, onConfirm, onCancel }) {
  const { t } = useTranslation();
  const [channels, setChannels] = useState(null);
  const [keep, setKeep] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChannels(null); setKeep([]);
    api.get("/channels").then(({ data }) => {
      setChannels(data);
      const active = data.filter((c) => c.active !== false).map((c) => c.id);
      setKeep(active.slice(0, limit));
    }).catch((e) => { toast.error(apiErr(e)); setChannels([]); });
  }, [open, limit]);

  if (!open) return null;

  const toggle = (id) => {
    setKeep((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= limit) {
        if (limit === 1) return [id];
        toast.info(t("keep.maxToast", { limit }));
        return prev;
      }
      return [...prev, id];
    });
  };

  const confirm = async () => {
    if (keep.length === 0) { toast.warning(t("keep.atLeastOne")); return; }
    setSaving(true);
    try { await onConfirm(keep); }
    finally { setSaving(false); }
  };

  const working = saving || extBusy;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 overflow-y-auto" data-testid="channel-keep-overlay">
      <div className="min-h-full flex items-start sm:items-center justify-center sm:p-6">
        <div onClick={(e) => e.stopPropagation()} data-testid="channel-keep-modal"
          className="relative bg-white w-full sm:max-w-lg min-h-screen sm:min-h-0 sm:rounded-2xl shadow-2xl p-5 sm:p-7">
          {onCancel && (
            <button data-testid="channel-keep-close" onClick={onCancel} className="absolute right-4 top-4 text-slate-400 hover:text-slate-700 p-1"><X size={24} /></button>
          )}
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 pr-8 mb-1">
            <Tv className="text-blue-600" size={24} /> {title || t("keep.defaultTitle")}
          </h2>
          <p className="text-base text-slate-600 mb-4">{description}</p>

          {channels === null ? (
            <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-blue-600" size={32} /></div>
          ) : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto vb-scroll">
              {channels.map((ch, i) => {
                const sel = keep.includes(ch.id);
                return (
                  <button key={ch.id} data-testid={`keep-channel-${ch.id}`} onClick={() => toggle(ch.id)} disabled={working}
                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${sel ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                    {ch.youtube?.thumbnail ? (
                      <img src={ch.youtube.thumbnail} alt={ch.name} className="rounded-lg w-11 h-11 object-cover shrink-0" />
                    ) : (
                      <div className={`${COLORS[i % COLORS.length]} text-white rounded-lg w-11 h-11 flex items-center justify-center text-lg font-bold shrink-0`}>{(ch.name || "C").charAt(0).toUpperCase()}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 truncate">{ch.name}</div>
                      <div className="text-sm text-slate-500">{t("keep.videoCount", { count: ch.video_count })}{ch.active === false ? t("keep.disabledSuffix") : ""}</div>
                    </div>
                    <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${sel ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-300"}`}>
                      {sel ? <Check size={18} strokeWidth={3} /> : <Lock size={15} />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 mt-5">
            <span className="text-sm font-semibold text-slate-500">{t("keep.activeCount", { count: keep.length, limit })}</span>
            <div className="flex gap-2">
              {onCancel && <button data-testid="channel-keep-cancel" onClick={onCancel} disabled={working} className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-slate-200 font-bold text-slate-600 hover:bg-slate-50">{t("keep.cancel")}</button>}
              <button data-testid="channel-keep-confirm" onClick={confirm} disabled={working || keep.length === 0}
                className="min-h-[48px] px-4 sm:px-5 text-base rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {working ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} {confirmLabel || t("common.save")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
